/* eslint-disable @typescript-eslint/no-explicit-any */
import { Edit as EditIcon } from '@mui/icons-material';
import { Button, IconButton, Switch } from '@mui/material';
import React from 'react';
import AdminLayoutView from 'src/Layout/AdminLayout/AdminLayoutView';
import AdminTabs from 'src/Layout/AdminLayout/AdminTabs';
import { AdminTabValue } from 'src/Layout/AdminLayout/AdminTabs/types';
import AdminViewWrapper from 'src/Layout/AdminLayout/AdminViewWrapper';
import GridData from 'src/components/GridData/GridData';
import { TableHeader, TableHeaderItem, TABLE_HEADER_ACTION_SX } from 'src/components/TableHeader/TableHeader';
import { Dropdown } from 'src/UIKit';
import useConfirmationDialog from 'src/components/ConfirmationDialog/view';
import { useAdminCampaignsViewController } from '../../controllers';
import { useMemo, useState, useEffect } from 'react';
import { useApplicationsApi } from '../../../applications/api';
import { CampaignConfigCategoryView } from '../../../campaignConfigBuilder/types/CampaignConfigCategoryView';
import { useNavigate } from 'react-router-dom';
import { CellText } from '../CellText';
import { useCampaignConfigService } from 'src/modules/campaignConfigBuilder';

export const AdminCampaignsView = () => {
  const {
    isPageLoading,
    campaignConfigs,
    filters,
    totalCount,
    changeSearchFilter,
    changeTypeFilter,
    changeApplicationFilter,
    changeCategoryFilter,
    changeSorting,
    changePagination,
    confirmToggleStatus
  } = useAdminCampaignsViewController();

  const { openDialog, ConfirmationDialog } = useConfirmationDialog();

  const [applications, setApplications] = useState<any[]>([]);
  const [categories, setCategories] = useState<CampaignConfigCategoryView[]>([]);
  const applicationsApi = useApplicationsApi();
  const campaignConfigService = useCampaignConfigService();
  const navigate = useNavigate();

  // Fetch applications + categories for column display and the filter dropdowns.
  // Run once on mount: applicationsApi/campaignConfigService are recreated each
  // render and are not stable deps. Both fetches are independent, so fire in parallel.
  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const response = await applicationsApi.getAdminApplications({
          limit: 1000,
          offset: 0
        });
        setApplications(response.data.items || []);
      } catch (error) {
        console.error('Failed to fetch applications:', error);
        setApplications([]);
      }
    };

    const fetchCategories = async () => {
      try {
        const response = await campaignConfigService.getCampaignConfigCategories({
          limit: 1000,
          offset: 0,
          statuses: 'active',
        });
        setCategories(response.items || []);
      } catch (error) {
        console.error('Failed to fetch campaign config categories:', error);
        setCategories([]);
      }
    };

    fetchApplications();
    fetchCategories();
  }, []);

  // Create a map of application ID to name for quick lookup
  const applicationMap = useMemo(() => {
    const map: Record<string, string> = {};
    applications.forEach(app => {
      map[app.id] = app.name || `Application ${app.id}`;
    });
    return map;
  }, [applications]);

  // Create a map of category name to title for quick lookup
  const categoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    categories.forEach(category => {
      map[category.name] = category.title || category.name;
    });
    return map;
  }, [categories]);

  const applicationOptions = useMemo(() => [
    { value: '', title: 'All Applications' },
    ...Object.entries(applicationMap).map(([value, title]) => ({ value, title })),
  ], [applicationMap]);

  const categoryOptions = useMemo(() => [
    { value: '', title: 'All Categories' },
    ...Object.entries(categoryMap).map(([value, title]) => ({ value, title })),
  ], [categoryMap]);

  const campaignTypeOptions = useMemo(() => [
    { value: '', title: 'All Campaign Types' },
    { value: 'message', title: 'Message' },
    { value: 'join', title: 'Join' },
    { value: 'reminder', title: 'Reminder' },
    { value: 'annual', title: 'Annual' },
    { value: 'away_message', title: 'Away Message' },
  ], []);


  return (
    <>
      <ConfirmationDialog />

      <AdminViewWrapper>
        <AdminLayoutView tabs={<AdminTabs value={AdminTabValue.campaigns} />}>
          <GridData
            header={
              <TableHeader
                searchValue={filters.query || undefined}
                onSearchChange={changeSearchFilter}
                searchPlaceholder="Search campaigns"
                filters={
                  <>
                    <TableHeaderItem>
                      <Dropdown
                        label="Campaign Types"
                        value={filters.types || undefined}
                        options={campaignTypeOptions}
                        onChange={(_, v) => changeTypeFilter(v)}
                        margin="none"
                      />
                    </TableHeaderItem>

                    <TableHeaderItem>
                      <Dropdown
                        label="Application"
                        value={filters.applicationIds || undefined}
                        options={applicationOptions}
                        onChange={(_, v) => changeApplicationFilter(v)}
                        margin="none"
                      />
                    </TableHeaderItem>

                    <TableHeaderItem>
                      <Dropdown
                        label="Categories"
                        value={filters.categories || undefined}
                        options={categoryOptions}
                        onChange={(_, v) => changeCategoryFilter(v)}
                        margin="none"
                      />
                    </TableHeaderItem>
                  </>
                }
                actions={
                  <Button
                    onClick={() => {
                      navigate(`/campaign-config-builder`);
                    }}
                    variant="contained"
                    sx={TABLE_HEADER_ACTION_SX}
                  >
                    New Campaign
                  </Button>
                }
              />
            }
            isLoading={isPageLoading}
            rows={campaignConfigs}
            columns={[
              {
                field: "name",
                headerName: "Campaign",
                minWidth: 200,
                editable: false,
                flex: 1,
                renderCell: (params) => {
                  const campaignName = typeof params.row.config === 'string' ? params.row.config : (params.row.config as Record<string, any>).name;
                  return (
                    <CellText muted={params.row.status === 'archived'}>
                      {campaignName}
                    </CellText>
                  );
                },
              },
              {
                field: "type",
                headerName: "Internal Type",
                minWidth: 150,
                editable: false,
                flex: 1,
                renderCell: (params) => {
                  const campaignType = typeof params.row.config === 'string' ? params.row.config : (params.row.config as Record<string, any>).type;
                  return (
                    <CellText muted={params.row.status === 'archived'}>
                      {campaignType}
                    </CellText>
                  );
                }
              },     
              {
                field: "application_name",
                headerName: "Application",
                minWidth: 150,
                sortable: true,
                editable: false,
                flex: 1,
                renderCell: (params) => {
                  const applicationIds = params.row.applicationIds;
                  let displayText = 'Global';

                  // If applicationIds is null or empty, show Global
                  if (!applicationIds || (Array.isArray(applicationIds) && applicationIds.length === 0)) {
                    displayText = 'Global';
                  }
                  // If applicationIds is an array, map to names
                  else if (Array.isArray(applicationIds)) {
                    const appNames = applicationIds.map(id => applicationMap[id] || `Application ${id}`);
                    displayText = appNames.join(', ');
                  }

                  return (
                    <CellText muted={params.row.status === 'archived'}>
                      {displayText}
                    </CellText>
                  );
                },
              },
              {
                field: "category",
                headerName: "Campaign Category",
                minWidth: 180,
                sortable: true,
                editable: false,
                flex: 1,
                renderCell: (params) => {
                  const config = params.row.config;
                  const categoryName = typeof config === 'string' ? undefined : (config as Record<string, any>)?.category;
                  const displayText = categoryName ? (categoryMap[categoryName] || categoryName) : '-';

                  return (
                    <CellText muted={params.row.status === 'archived'}>
                      {displayText}
                    </CellText>
                  );
                },
              },
              {
                field: "updatedAt",
                headerName: "Last Updated",
                minWidth: 150,
                sortable: false,
                editable: false,
                flex: 1,
                renderCell: (params) => {
                  const updatedAt = params.row.updatedAt;
                  let displayText = '-';

                  if (updatedAt) {
                    try {
                      const date = new Date(updatedAt);
                      displayText = date.toLocaleDateString();
                    } catch {
                      displayText = updatedAt;
                    }
                  }

                  return (
                    <CellText muted={params.row.status === 'archived'}>
                      {displayText}
                    </CellText>
                  );
                },
              },
              {
                field: "updatedBy",
                headerName: "Updated By",
                minWidth: 150,
                sortable: false,
                editable: false,
                flex: 1,
                renderCell: (params) => {
                  const updatedBy = params.row.updatedBy;
                  let displayText = '';

                  if (updatedBy && updatedBy.firstName && updatedBy.lastName) {
                    displayText = `${updatedBy.firstName} ${updatedBy.lastName}`;
                  }

                  return (
                    <CellText muted={params.row.status === 'archived'}>
                      {displayText}
                    </CellText>
                  );
                },
              },
            ]}
            renderAction={(row) => {
              return (
                <>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => {
                      navigate(`/campaign-config-builder/${row.id}`);
                    }}
                  >
                    <EditIcon fontSize="inherit" />
                  </IconButton>
                  <Switch
                    size="small"
                    checked={row.status === 'active'}
                    onChange={() => {
                      const campaignName = typeof row.config === 'string' ? row.config : (row.config as Record<string, any>).name;
                      const newStatus = row.status === 'active' ? 'archived' : 'active';
                      const action = newStatus === 'active' ? 'activate' : 'deactivate';
                      openDialog({
                        title: `${action.charAt(0).toUpperCase() + action.slice(1)} "${campaignName}"`,
                        content: `By ${action === 'activate' ? 'activating' : 'deactivating'} this campaign you will ${action === 'activate' ? 'allow' : 'prevent'} users from being able to create new campaigns of this type.`,
                        onConfirm: () => confirmToggleStatus(row),
                        confirmButtonText: action.charAt(0).toUpperCase() + action.slice(1),
                        confirmButtonColor: action === 'activate' ? 'primary' : 'error',
                        cancelButtonVariant: 'outlined',
                      });
                    }}
                  />
                </>
              );
            }}
            sorting={{
              value: filters.sort,
              onChange: changeSorting,
            }}
            pagination={{
              page: Math.floor((filters.offset || 0) / (filters.limit || 25)),
              pageSize: filters.limit || 25,
              totalCount: totalCount,
              onChange: changePagination,
            }}
          />
        </AdminLayoutView>
      </AdminViewWrapper>
    </>
  );
};
